import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { api, type MandatoryDocumentStatus } from '../api/client';
import { LectoresDelDocumento } from '../components/LectoresDelDocumento';
import { BarraDeFiltros, Buscador, LimpiarFiltros, Segmentado } from '../components/Filtros';
import { AvisoError } from '../components/Aviso';
import { claveVisible, textoBuscable } from '../documentos/clave';

/**
 * Quién tiene que leer un documento obligatorio, desde el reporte.
 *
 * **Es la misma tabla que la ficha del documento**, no una parecida. Antes eran
 * dos: ésta sólo listaba pendientes y no dejaba hacer nada con ellos, así que
 * quien llegaba desde el menú veía el problema y no tenía cómo empujarlo —
 * tenía que ir a buscar el documento por su cuenta. Dos listas de lo mismo
 * terminan divergiendo, y la que se queda atrás es siempre la que alguien está
 * mirando.
 */
function Detalle({ documentId }: { documentId: string }) {
  const { t } = useTranslation();

  return (
    <>
      <div className="pagina__encabezado">
        <Link className="migaja" to="/lecturas">
          {t('reading.backToOverview')}
        </Link>
        <h1 className="pagina__titulo">{t('reading.pendingReaders')}</h1>
        <Link className="boton boton--discreto" to={`/documentos/${documentId}`}>
          {t('reading.openDocument')}
        </Link>
      </div>

      <LectoresDelDocumento documentId={documentId} />
    </>
  );
}

/**
 * Lecturas obligatorias: qué hay que leer y cuánto rezago lleva cada cosa.
 *
 * Los de más rezago van arriba —así los ordena el servidor— porque esta pantalla
 * se abre para saber qué hace falta empujar, no para inventariar. El total de la
 * audiencia va al lado del rezago a propósito: «3 pendientes» no dice si eso es
 * el principio o el final.
 */
/** Compara sin acentos ni mayúsculas: quien escribe «credito» busca «Crédito». */
const coincide = (texto: string, busca: string) =>
  texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .includes(
      busca
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase(),
    );

export function Lecturas() {
  const { t } = useTranslation();
  const { documentId } = useParams();
  const [documentos, setDocumentos] = useState<MandatoryDocumentStatus[] | null>(null);
  const [busca, setBusca] = useState('');
  const [rezago, setRezago] = useState('');
  const [error, setError] = useState<unknown>(null);

  const cargar = useCallback(async () => {
    try {
      setDocumentos(await api.mandatoryOverview());
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, []);

  useEffect(() => {
    if (!documentId) void cargar();
  }, [cargar, documentId]);

  if (documentId) return <Detalle documentId={documentId} />;

  const todos = documentos ?? [];
  const conRezago = todos.filter((d) => d.overdue > 0).length;
  const visibles = todos.filter(
    (d) =>
      (!busca || coincide(textoBuscable(d), busca)) &&
      (!rezago || (rezago === 'rezago' ? d.overdue > 0 : d.overdue === 0)),
  );

  return (
    <>
      <div className="pagina__encabezado">
        <h1 className="pagina__titulo">{t('reading.overviewTitle')}</h1>
        <p className="pagina__bajada">{t('reading.overviewExplanation')}</p>
      </div>

      <AvisoError error={error} />

      <section className="tarjeta">
        <div className="tarjeta__encabezado">
          <h2 className="tarjeta__titulo">{t('reading.mandatoryDocuments')}</h2>
          {conRezago > 0 && (
            <span className="pildora pildora--critico">
              <span className="pildora__punto" aria-hidden="true" />
              {t('reading.documentsOverdue', { count: conRezago })}
            </span>
          )}
        </div>

        <BarraDeFiltros>
          <Buscador id="buscarLectura" etiqueta={t('documents.search')} valor={busca} onCambio={setBusca} />
          <Segmentado
            etiqueta={t('reading.status')}
            valor={rezago}
            onCambio={setRezago}
            opciones={[
              { valor: '', etiqueta: t('users.allStatuses'), conteo: todos.length },
              { valor: 'rezago', etiqueta: t('reading.overdue'), conteo: conRezago },
              { valor: 'alcorriente', etiqueta: t('reading.upToDate'), conteo: todos.length - conRezago },
            ]}
          />
          <div className="barra-filtros__acciones">
            <LimpiarFiltros
              hay={Boolean(busca || rezago)}
              onLimpiar={() => {
                setBusca('');
                setRezago('');
              }}
            />
          </div>
        </BarraDeFiltros>

        {documentos && visibles.length === 0 ? (
          <div className="vacio">
            <span className="vacio__titulo">
              {busca || rezago ? t('common.noResults') : t('reading.noneMandatory')}
            </span>
            {!busca && !rezago && <span className="campo__ayuda">{t('reading.noneMandatoryHelp')}</span>}
          </div>
        ) : (
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  <th>{t('documents.name')}</th>
                  <th>{t('documents.version')}</th>
                  <th>{t('reading.deadline')}</th>
                  <th>{t('reading.progress')}</th>
                  <th>{t('reading.overdue')}</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((documento) => (
                  <tr key={documento.documentId}>
                    <td>
                      <Link to={`/lecturas/${documento.documentId}`}>{documento.title}</Link>
                      <div className="campo__ayuda mono">{claveVisible(documento)}</div>
                    </td>
                    <td className="mono">{documento.versionLabel}</td>
                    <td className="mono">
                      {t('reading.daysCount', { days: documento.deadlineDays })}
                    </td>
                    <td className="mono">
                      {/* Leídos sobre el total. Sin el total, un número suelto no
                          dice si va bien o mal. */}
                      {documento.acknowledged} / {documento.audience}
                    </td>
                    <td>
                      {documento.overdue > 0 ? (
                        <span className="pildora pildora--critico">
                          <span className="pildora__punto" aria-hidden="true" />
                          {documento.overdue}
                        </span>
                      ) : (
                        <span className="dato__valor">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
